/**
 * useWorkflowGuardActions - Catálogo de ações que uma Exigência pode bloquear
 *
 * Lê a RPC segura `get_workflow_guard_action_catalog` (SECURITY DEFINER, com
 * validação de tenant). O catálogo é extensível: além dos itens globais, cada
 * empresa pode ter itens próprios em `workflow_guard_action_types`.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (bloco de Exigência robusto).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useWorkflowGuardActions');

/** Item do catálogo de ações bloqueáveis */
export interface WorkflowGuardAction {
  code: string;
  label: string;
  module: string;
  description: string | null;
}

/** Rótulo amigável de cada módulo (usado para agrupar a lista) */
export const ROTULO_MODULO: Record<string, string> = {
  crm: 'CRM / Negócio',
  proposal: 'Propostas',
  contract: 'Contratos',
  financial: 'Financeiro',
  task: 'Tarefas',
  // v1.1.0: sinônimo antigo do catálogo (unificado em 'task')
  tasks: 'Tarefas',
  retention: 'Retenção',
  legal: 'Jurídico',
  conversation: 'Conversas',
  vehicle: 'Veículos',
  workflow: 'Processo',
};

/** Busca o catálogo de ações bloqueáveis do tenant atual */
export function useWorkflowGuardActions(enabled = true) {
  return useQuery({
    queryKey: ['workflow-guard-actions'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<WorkflowGuardAction[]> => {
      const { data, error } = await supabase.rpc('get_workflow_guard_action_catalog', {
        p_tenant_hint: undefined,
      });

      if (error) {
        logger.error('Falha ao carregar catálogo de ações bloqueáveis', error);
        throw error;
      }

      return (data ?? []) as WorkflowGuardAction[];
    },
  });
}
